import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import { getMIPInvestorHoldingsData } from '@dealstack/frontend/shared/dataExchange/services/mip-manage.service.ts';
import { toHoldingRows } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/HoldingsCard/investorHoldings.utils.ts';
import { InvestorHoldingRow } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/HoldingsCard/investorHoldings.types.ts';

interface Result {
	rows: InvestorHoldingRow[];
	isLoading: boolean;
	isError: boolean;
}

/**
 * Holdings by or for the investor, vehicles included — the endpoint unions the investor's own
 * rows with those held for them, so no fan-out per person is needed. One query also keeps
 * `data` referentially stable, which the table relies on.
 */
export const useInvestorHoldings = ({ investorId }: { investorId: string }): Result => {
	const holdingsQuery = useQuery({
		queryKey: [API_PATHS.MIP_INVESTOR_HOLDINGS_ID_GET, { investorId }],
		queryFn: ({ signal }) =>
			getMIPInvestorHoldingsData(
				API_PATHS.MIP_INVESTOR_HOLDINGS_ID_GET,
				{ investorId },
				signal
			),
	});

	const payload = holdingsQuery.data?.payload;

	const rows = useMemo(() => toHoldingRows(payload), [payload]);

	return {
		rows,
		isLoading: holdingsQuery.isPending,
		isError: holdingsQuery.isError,
	};
};
